import { useState, useMemo } from 'react'
import { colors, formatNumber } from '../styles/tokens'
import { Button, Input, Select } from './FormControls'
import { calculateMepEquipment } from '../lib/quantityEngine'
import { useDraftPersistence } from '../lib/useDraftPersistence'

export default function MepEquipmentModulePanel({ scopeId, boqItemId, superseding, onClose, onError, saving, onSave }) {
  const draftKey = `draft:mep_equipment:${scopeId || boqItemId || 'unknown'}`
  const [draft, setDraft, clearDraft] = useDraftPersistence(draftKey, { items: [] })
  const initial = superseding ? { items: superseding.engineering_inputs?.items || [] } : draft
  const [items, setItemsRaw] = useState(initial.items)
  const setItems = (u) => setItemsRaw((p) => { const n = typeof u === 'function' ? u(p) : u; if (!superseding) setDraft((d) => ({ ...d, items: n })); return n })

  function addItem() { setItems((p) => [...p, { id: `I${Date.now()}`, equipmentType: '', discipline: 'mechanical', geometryCount: '', referenceQuantity: '', referenceUnit: '' }]) }
  function updateItem(id, patch) { setItems((p) => p.map((i) => (i.id === id ? { ...i, ...patch } : i))) }
  function removeItem(id) { setItems((p) => p.filter((i) => i.id !== id)) }

  const itemsForCalc = useMemo(() => items.map((i) => ({
    equipmentType: i.equipmentType, discipline: i.discipline,
    geometryCount: i.geometryCount === '' ? NaN : Number(i.geometryCount),
    referenceQuantity: i.referenceQuantity === '' ? null : Number(i.referenceQuantity),
    referenceUnit: i.referenceUnit || null,
  })), [items])

  const result = useMemo(() => {
    if (itemsForCalc.length === 0) return { value: null, error: null }
    try { return { value: calculateMepEquipment(itemsForCalc), error: null } } catch (e) { return { value: null, error: e.message } }
  }, [itemsForCalc])

  async function handleSave() {
    if (!result.value) return
    const engineeringInputs = { items }
    const succeeded = await onSave({ calculatedValueTon: result.value.finalQuantity, unit: 'no', geometrySnapshot: { items: [] }, engineeringInputs, breakdownSnapshot: { steps: result.value.steps, itemBreakdown: result.value.itemBreakdown }, takeoffItemIdForProvenance: null })
    if (succeeded !== false) clearDraft()
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <label style={{ fontSize: 11, fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase' }}>Equipment</label>
        <Button size="sm" variant="secondary" onClick={addItem}>+ Add Equipment</Button>
      </div>
      {items.map((i) => (
        <div key={i.id} style={{ border: `1px solid ${colors.border}`, borderRadius: 6, padding: 8, marginBottom: 8 }}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
            <Input placeholder="Equipment Type (e.g. AHU)" value={i.equipmentType} onChange={(e) => updateItem(i.id, { equipmentType: e.target.value })} style={{ flex: 1, fontSize: 11 }} />
            <button onClick={() => removeItem(i.id)} style={{ background: 'none', border: 'none', color: colors.danger, cursor: 'pointer' }}>✕</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(90px, 1fr))', gap: 6 }}>
            <L label="Discipline"><Select value={i.discipline} onChange={(e) => updateItem(i.id, { discipline: e.target.value })} style={{ fontSize: 11, width: '100%' }}><option value="mechanical">Mechanical</option><option value="electrical">Electrical</option></Select></L>
            <L label="Geometry Count (No.)"><Input type="number" value={i.geometryCount} onChange={(e) => updateItem(i.id, { geometryCount: e.target.value })} style={{ fontSize: 11, width: '100%' }} /></L>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginTop: 6 }}>
            <L label="Reference Quantity (optional, informational)"><Input type="number" value={i.referenceQuantity} onChange={(e) => updateItem(i.id, { referenceQuantity: e.target.value })} style={{ fontSize: 11, width: '100%' }} /></L>
            <L label="Reference Unit (e.g. kW)"><Input value={i.referenceUnit} onChange={(e) => updateItem(i.id, { referenceUnit: e.target.value })} style={{ fontSize: 11, width: '100%' }} /></L>
          </div>
        </div>
      ))}
      {result.value && (
        <div style={{ margin: '12px 0' }}>
          {result.value.itemBreakdown.map((ib, idx) => (
            <div key={idx} style={{ border: `1px solid ${colors.border}`, borderRadius: 8, padding: 10, marginBottom: 8, background: '#FAFBFC', fontSize: 11 }}>
              <strong>{ib.item.equipmentType}</strong> ({ib.item.discipline}): {ib.item.geometryCount} No.
              {ib.item.referenceQuantity != null && <span style={{ color: colors.textMuted }}> — Ref: {ib.item.referenceQuantity} {ib.item.referenceUnit} (informational only)</span>}
            </div>
          ))}
          <div style={{ padding: '10px 12px', background: '#F8FAFF', border: `1px solid ${colors.primary}`, borderRadius: 8, fontWeight: 700 }}>Total: {formatNumber(result.value.finalQuantity)} No.</div>
        </div>
      )}
      {result.error && <div style={{ fontSize: 12, color: colors.textMuted, fontStyle: 'italic', marginBottom: 14 }}>{result.error}</div>}
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        {!superseding && <button onClick={() => { if (window.confirm('Discard draft?')) { clearDraft(); onClose() } }} style={{ background: 'none', border: 'none', color: colors.danger, cursor: 'pointer', fontSize: 12 }}>Discard Draft</button>}
        <Button onClick={handleSave} disabled={!result.value || saving} style={{ marginLeft: 'auto' }}>{saving ? 'Saving…' : superseding ? 'Save New Version' : 'Save Output'}</Button>
      </div>
    </div>
  )
}
function L({ label, children }) { return <div><label style={{ display: 'block', fontSize: 10, color: colors.textMuted, marginBottom: 2 }}>{label}</label>{children}</div> }
